import { useCallback, useEffect, useRef, useState } from "react";
import tw from "twin.macro";
import { Pause } from "lucide-react";

// Morph de couleur du remplissage : rouge orangé doux (modules.redBase) →
// rouge vif profond, au fur et à mesure de l'appui maintenu.
const SOFT: [number, number, number] = [219, 98, 67];
const VIVID: [number, number, number] = [153, 20, 20];

const lerp = (a: number, b: number, t: number) => Math.round(a + (b - a) * t);
const fillColor = (t: number) =>
  `rgb(${lerp(SOFT[0], VIVID[0], t)}, ${lerp(SOFT[1], VIVID[1], t)}, ${lerp(
    SOFT[2],
    VIVID[2],
    t
  )})`;

/**
 * Bouton "arrêt d'urgence" à appui maintenu (hold-to-confirm) : il faut garder
 * le doigt appuyé pendant `holdDurationMs` pour déclencher `onComplete`. La
 * barre de remplissage progresse de la gauche vers la droite et sa couleur
 * morphe de rouge doux à rouge vif. Relâcher avant la fin annule (reset animé).
 * → évite les miss-click tout en gardant une action rapide (~0,75 s).
 */
function EmergencyStopHoldButton({
  disabled,
  label,
  onComplete,
  holdDurationMs = 500,
}: {
  disabled: boolean;
  label: string;
  onComplete: () => void;
  holdDurationMs?: number;
}) {
  const [progress, setProgress] = useState(0);
  const [holding, setHolding] = useState(false);
  const rafRef = useRef<number | null>(null);
  const startRef = useRef<number | null>(null);

  const stopRaf = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const tick = useCallback(
    (now: number) => {
      if (startRef.current === null) startRef.current = now;
      const p = Math.min((now - startRef.current) / holdDurationMs, 1);
      setProgress(p);

      if (p >= 1) {
        stopRaf();
        setHolding(false);
        setProgress(0);
        startRef.current = null;
        onComplete();
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    },
    [holdDurationMs, onComplete, stopRaf]
  );

  const startHold = useCallback(() => {
    if (disabled) return;
    setHolding(true);
    startRef.current = null;
    stopRaf();
    rafRef.current = requestAnimationFrame(tick);
  }, [disabled, stopRaf, tick]);

  const cancelHold = useCallback(() => {
    stopRaf();
    startRef.current = null;
    setHolding(false);
    setProgress(0);
  }, [stopRaf]);

  // Nettoyage du rAF au démontage.
  useEffect(() => stopRaf, [stopRaf]);

  return (
    <button
      type="button"
      disabled={disabled}
      onPointerDown={startHold}
      onPointerUp={cancelHold}
      onPointerLeave={cancelHold}
      onPointerCancel={cancelHold}
      onContextMenu={(e) => e.preventDefault()}
      css={[
        tw`relative w-full overflow-hidden m-1 min-w-[130px]`,
        tw`flex items-center justify-center min-h-[44px] rounded-3xl p-1`,
        tw`text-white shadow-md ring-2 ring-orange-400/40`,
        tw`disabled:opacity-40 disabled:cursor-not-allowed`,
        // Fond "vide" = rouge doux de départ.
        { backgroundColor: fillColor(0) },
        // Promotion GPU (cf. StepActionButton).
        { transform: "translateZ(0)", touchAction: "none", userSelect: "none" },
      ]}
    >
      {/* Couche de remplissage (gauche → droite) avec morph de couleur. */}
      <div
        aria-hidden
        css={[tw`absolute inset-y-0 left-0`]}
        style={{
          width: `${progress * 100}%`,
          backgroundColor: fillColor(progress),
          transition: holding
            ? "none"
            : "width 250ms ease-out, background-color 250ms ease-out",
        }}
      />
      <span css={[tw`relative z-10 flex items-center gap-2`]}>
        <Pause tw="h-5 w-5 text-white" fill="white" strokeWidth={0.25} />
        <span css={[tw`text-lg font-normal`]}>{label}</span>
      </span>
    </button>
  );
}

export default EmergencyStopHoldButton;
